/**
 * Cost model for Writer telemetry.
 *
 * The ledger books `credit_equivalent = 0` during the admin beta, so the real
 * spend has to be derived from the token counts Vertex returns. Prices are
 * indicative Vertex AI list prices (EU multi-region) in USD per 1M tokens and
 * are kept here as auditable constants — adjust in one place when they shift.
 */

export interface ModelPrice {
  /** USD per 1M uncached input tokens. */
  input: number;
  /** USD per 1M cached input tokens. */
  cachedInput: number;
  /** USD per 1M output tokens (thinking tokens bill at the output rate). */
  output: number;
}

/** Standard list price plus an optional promotional rate with an end date. */
export interface ModelPriceSchedule extends ModelPrice {
  promo?: ModelPrice & { until: string };
}

export const USD_TO_EUR = 0.92;

/**
 * End of Google's introductory pricing window for Gemini 3.6 / 3.7 Flash
 * (inclusive of 31 Dec 2026; standard rates apply from 1 Jan 2027).
 */
export const PROMO_END_3X_FLASH = '2026-12-31T23:59:59.999Z';

export const MODEL_PRICES: Record<string, ModelPriceSchedule> = {
  // Vertex/Gemini standard (non-batch) paid-tier list prices, USD per 1M tokens.
  // Source: Gemini Enterprise Agent Platform pricing page, verified 2026-08-24.
  // 3.6 and 3.7 Flash share an introductory rate of 0.75 / 0.075 / 3.75
  // through 2026-12-31, rising to 1.50 / 0.15 / 7.50 on 2027-01-01.
  'gemini-3.5-flash': { input: 1.5, cachedInput: 0.15, output: 9.0 },
  'gemini-3.5-flash-lite': { input: 0.3, cachedInput: 0.03, output: 2.5 },
  'gemini-3.6-flash': {
    input: 1.5,
    cachedInput: 0.15,
    output: 7.5,
    promo: { input: 0.75, cachedInput: 0.075, output: 3.75, until: PROMO_END_3X_FLASH },
  },
  'gemini-3.7-flash': {
    input: 1.5,
    cachedInput: 0.15,
    output: 7.5,
    promo: { input: 0.75, cachedInput: 0.075, output: 3.75, until: PROMO_END_3X_FLASH },
  },
};

export interface TokenBreakdown {
  /** Total prompt tokens as reported (INCLUDES cached tokens). */
  tokensIn: number;
  tokensCached: number;
  tokensThinking: number;
  tokensOut: number;
  tokensTotal: number;
}

export const EMPTY_TOKENS: TokenBreakdown = {
  tokensIn: 0,
  tokensCached: 0,
  tokensThinking: 0,
  tokensOut: 0,
  tokensTotal: 0,
};

/** Effective price at a point in time (defaults to now). */
export function priceFor(
  model: string | null | undefined,
  at: Date | string | number = new Date(),
): ModelPrice | null {
  if (!model) return null;
  const entry = MODEL_PRICES[model];
  if (!entry) return null;
  if (entry.promo) {
    const t = at instanceof Date ? at.getTime() : new Date(at).getTime();
    if (Number.isFinite(t) && t <= Date.parse(entry.promo.until)) {
      return { input: entry.promo.input, cachedInput: entry.promo.cachedInput, output: entry.promo.output };
    }
  }
  return { input: entry.input, cachedInput: entry.cachedInput, output: entry.output };
}

/** Standard (post-promotional) list price — used for published pricing copy. */
export function standardPriceFor(model: string | null | undefined): ModelPrice | null {
  if (!model) return null;
  const entry = MODEL_PRICES[model];
  if (!entry) return null;
  return { input: entry.input, cachedInput: entry.cachedInput, output: entry.output };
}

/**
 * Cost in EUR for one model's token usage. Returns `null` when the model is
 * unknown — an unknown price is reported as unknown, never silently as 0.
 */
export function costEur(
  model: string | null | undefined,
  t: TokenBreakdown,
  at: Date | string | number = new Date(),
): number | null {
  const p = priceFor(model, at);
  if (!p) return null;
  const cached = Math.max(0, t.tokensCached);
  const uncachedIn = Math.max(0, t.tokensIn - cached);
  const out = Math.max(0, t.tokensOut) + Math.max(0, t.tokensThinking);
  const usd =
    (uncachedIn / 1_000_000) * p.input +
    (cached / 1_000_000) * p.cachedInput +
    (out / 1_000_000) * p.output;
  return usd * USD_TO_EUR;
}

/**
 * Cost at the STANDARD rate, ignoring any promotion. Published plan copy is
 * quoted from this so advertised numbers never oscillate with a temporary
 * introductory price.
 */
export function standardCostEur(
  model: string | null | undefined,
  t: TokenBreakdown,
): number | null {
  const p = standardPriceFor(model);
  if (!p) return null;
  const cached = Math.max(0, t.tokensCached);
  const uncachedIn = Math.max(0, t.tokensIn - cached);
  const out = Math.max(0, t.tokensOut) + Math.max(0, t.tokensThinking);
  const usd =
    (uncachedIn / 1_000_000) * p.input +
    (cached / 1_000_000) * p.cachedInput +
    (out / 1_000_000) * p.output;
  return usd * USD_TO_EUR;
}

export function addTokens(a: TokenBreakdown, b: TokenBreakdown): TokenBreakdown {
  return {
    tokensIn: a.tokensIn + b.tokensIn,
    tokensCached: a.tokensCached + b.tokensCached,
    tokensThinking: a.tokensThinking + b.tokensThinking,
    tokensOut: a.tokensOut + b.tokensOut,
    tokensTotal: a.tokensTotal + b.tokensTotal,
  };
}

export function formatEur(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—';
  if (n === 0) return '€0.00';
  if (n < 0.01) return `€${n.toFixed(4)}`;
  if (n < 1) return `€${n.toFixed(3)}`;
  return `€${n.toFixed(2)}`;
}

/* -------------------------------------------------------------------------
 * Credit meter (mirrors supabase/functions/_shared/model-pricing.ts)
 * ---------------------------------------------------------------------- */

/**
 * One PaperVerse credit is priced against 7 eurocents of raw model cost.
 * At this denomination a writing turn costs roughly one credit.
 */
export const CREDIT_COST_BASE_EUR = 0.07;

/** Gross-margin multiplier applied to raw model cost. */
export const CREDIT_MARKUP = 3.0;

/** Credits are metered and displayed with one decimal. */
export const CREDIT_GRANULARITY = 0.1;

/**
 * Convert raw model cost into billable credits, rounded UP to the next 0.1.
 * Any non-zero spend costs at least 0.1 credit.
 */
export function creditsForCostEur(cost: number | null | undefined): number | null {
  if (cost === null || cost === undefined || !Number.isFinite(cost)) return null;
  if (cost <= 0) return 0;
  const raw = (cost / CREDIT_COST_BASE_EUR) * CREDIT_MARKUP;
  const steps = Math.ceil(raw / CREDIT_GRANULARITY - 1e-9);
  return Math.max(1, steps) * CREDIT_GRANULARITY;
}

/** Credits for a token mix on a given model, at the rate in force. */
export function creditsForTokens(
  model: string | null | undefined,
  t: TokenBreakdown,
  at: Date | string | number = new Date(),
): number | null {
  return creditsForCostEur(costEur(model, t, at));
}

/**
 * Credits quoted at the STANDARD rate. Published pricing copy uses this so
 * advertised workloads stay stable across the promotional cut-over — the
 * conservative number, which can only under-promise.
 */
export function creditsForTokensAtStandardRate(
  model: string | null | undefined,
  t: TokenBreakdown,
): number | null {
  return creditsForCostEur(standardCostEur(model, t));
}

export interface TypicalAction {
  id: string;
  label: string;
  detail: string;
  model: string;
  tokens: TokenBreakdown;
}

/**
 * Representative token mixes, taken from observed medians. They exist so the
 * pricing copy is derived from the same engine that bills, never hand-typed.
 */
/**
 * Representative token mixes, calibrated 2026-08-27 from the medians of
 * `ai_usage_events` over the preceding 45 days (feature = 'writer_turn' and
 * 'ai_review'). Re-derive these from the same query when usage patterns shift
 * — the pricing copy is rendered from them, so stale numbers become stale
 * promises. Costed at the standard (post-promotional) model rate.
 */
export const TYPICAL_ACTIONS: TypicalAction[] = [
  {
    id: 'question',
    label: 'A question about your draft',
    detail: 'The agent reads the relevant sections and answers',
    model: 'gemini-3.7-flash',
    tokens: {
      tokensIn: 40_000,
      tokensCached: 25_000,
      tokensThinking: 0,
      tokensOut: 900,
      tokensTotal: 40_900,
    },
  },
  {
    id: 'edit',
    label: 'A writing turn',
    detail: 'Drafting or revising a section, then recompiling',
    model: 'gemini-3.7-flash',
    tokens: {
      tokensIn: 65_000,
      tokensCached: 30_000,
      tokensThinking: 0,
      tokensOut: 1_400,
      tokensTotal: 66_400,
    },
  },
  {
    id: 'review',
    label: 'A full AI review',
    detail: 'Referee-style read of the whole manuscript',
    model: 'gemini-3.7-flash',
    tokens: {
      tokensIn: 33_400,
      tokensCached: 0,
      tokensThinking: 12_900,
      tokensOut: 2_600,
      tokensTotal: 48_900,
    },
  },
];


/** Round for display: one decimal, no trailing ".0". */
export function formatCreditsShort(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/* -------------------------------------------------------------------------
 * Plan workload translation
 * ---------------------------------------------------------------------- */

export interface PlanWorkload {
  /** Whole writing turns a monthly grant covers, if spent only on writing. */
  writingTurns: number;
  /** Full manuscript reviews a monthly grant covers, if spent only on reviews. */
  reviews: number;
}

const actionCredits = (id: string): number => {
  const action = TYPICAL_ACTIONS.find((a) => a.id === id);
  if (!action) return 1;
  return creditsForTokensAtStandardRate(action.model, action.tokens) ?? 1;
};

/**
 * Translate a monthly credit grant into the work it buys. Cards and the
 * explainer both read this, so the copy can never drift from the meter.
 */
export function planWorkload(monthlyCredits: number): PlanWorkload {
  const perTurn = actionCredits('edit');
  const perReview = actionCredits('review');
  return {
    writingTurns: Math.max(0, Math.floor(monthlyCredits / perTurn)),
    reviews: Math.max(0, Math.floor(monthlyCredits / perReview)),
  };
}
